import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, FolderTree, Award, Image as ImageIcon,
  MessageSquare, Wrench, Mail, Home, FileText, Layers,
  Building2, Search, Users, LogOut, ChevronDown, Menu, X,
} from "lucide-react";

/* ============ LOGIN ============ */
export function AdminLogin() {
  const nav = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Admin is never intended for search indexing.
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
    document.title = "Admin — Teknik Diesel Indonesia";
  }, []);

  useEffect(() => { if (user && user.email) nav("/admin"); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Login berhasil");
      nav("/admin");
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Email atau password salah";
      setErr(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-100 px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-neutral-200 rounded-xl shadow-sm p-8">
        <div className="mb-6 text-center">
          <img src="/logo.png" alt="Teknik Diesel Indonesia" className="h-14 w-auto mx-auto mb-4" style={{ maxWidth: "220px" }} />
          <div className="font-mono text-[10px] tracking-[0.2em] text-[#FF9418] uppercase">Admin Panel</div>
          <h1 className="font-display font-extrabold text-neutral-900 text-2xl mt-2">Masuk</h1>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-neutral-500 mb-1.5 block">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
              className="w-full bg-white border border-neutral-300 focus:border-[#FF9418] rounded-md px-3 py-2.5 text-sm text-neutral-900 outline-none transition"
              data-testid="login-email" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-neutral-500 mb-1.5 block">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full bg-white border border-neutral-300 focus:border-[#FF9418] rounded-md px-3 py-2.5 text-sm text-neutral-900 outline-none transition"
              data-testid="login-password" />
          </div>
          {err && <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{err}</div>}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-[#FF9418] hover:bg-[#e88410] text-white rounded-md uppercase tracking-wider font-bold text-sm" data-testid="login-submit">
            {loading ? "Memproses..." : "Masuk"}
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ============ SIDEBAR STRUCTURE ============ */
const NAV = [
  { type: "item", to: "/admin", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { type: "section", label: "Produk" },
  { type: "item", to: "/admin/products", icon: Package, label: "Produk", testid: "nav-products", match: "/admin/products" },
  { type: "item", to: "/admin/categories", icon: FolderTree, label: "Kategori", testid: "nav-categories" },
  { type: "item", to: "/admin/brands", icon: Award, label: "Brand", testid: "nav-brands" },
  { type: "item", to: "/admin/media", icon: ImageIcon, label: "Media", testid: "nav-media" },
  { type: "section", label: "Permintaan" },
  { type: "item", to: "/admin/enquiries/product", icon: MessageSquare, label: "Permintaan Produk", testid: "nav-enq-product" },
  { type: "item", to: "/admin/enquiries/spare-parts", icon: Wrench, label: "Permintaan Spare Parts", testid: "nav-enq-spare" },
  { type: "item", to: "/admin/enquiries/contact", icon: Mail, label: "Permintaan Kontak", testid: "nav-enq-contact" },
  { type: "section", label: "Konten" },
  { type: "item", to: "/admin/content/homepage", icon: Home, label: "Beranda", testid: "nav-homepage" },
  { type: "item", to: "/admin/content/articles", icon: FileText, label: "Artikel", testid: "nav-articles" },
  { type: "item", to: "/admin/content/pages", icon: Layers, label: "Halaman", testid: "nav-pages" },
  { type: "section", label: "Pengaturan" },
  { type: "item", to: "/admin/settings/business", icon: Building2, label: "Pengaturan Bisnis", testid: "nav-settings-business" },
  { type: "item", to: "/admin/settings/seo", icon: Search, label: "Pengaturan SEO", testid: "nav-settings-seo" },
  { type: "item", to: "/admin/settings/users", icon: Users, label: "Pengguna", testid: "nav-settings-users", adminOnly: true },
];

/* ============ SHELL ============ */
function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Admin routes must never be indexed by search engines.
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
    return () => { document.head.querySelector('meta[name="robots"]')?.remove(); };
  }, []);

  const onLogout = async () => { await logout(); toast.success("Logout berhasil"); nav("/admin/login"); };

  const isActive = (to, match) => {
    if (to === "/admin") return loc.pathname === "/admin";
    const target = match || to;
    return loc.pathname === target || loc.pathname.startsWith(target + "/");
  };

  const Sidebar = ({ mobile }) => (
    <aside className={`${mobile ? "w-72" : "w-64"} bg-white border-r border-neutral-200 flex flex-col h-full`}>
      <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="TDI" className="h-9 w-auto" style={{ maxWidth: "150px" }} />
        </div>
        {mobile && <button onClick={() => setMobileOpen(false)} className="text-neutral-500"><X size={18} /></button>}
      </div>
      <div className="px-4 py-2 border-b border-neutral-200">
        <div className="font-mono text-[10px] tracking-[0.2em] text-[#FF9418] uppercase">Admin Panel</div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        {NAV.map((it, idx) => {
          if (it.type === "section") {
            return (
              <div key={idx} className="px-5 pt-4 pb-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-400">
                {it.label}
              </div>
            );
          }
          // Hide admin-only items from non-admin roles (e.g. Editor).
          if (it.adminOnly && user?.role !== "admin") return null;
          const active = isActive(it.to, it.match);
          return (
            <Link key={it.to} to={it.to} onClick={() => setMobileOpen(false)}
              data-testid={it.testid}
              className={`flex items-center gap-3 mx-2 px-3 py-2 text-sm rounded-md transition ${
                active
                  ? "bg-[#FF9418]/10 text-[#FF9418] font-semibold"
                  : "text-neutral-700 hover:bg-neutral-50"
              }`}>
              <it.icon size={16} className={active ? "text-[#FF9418]" : "text-neutral-500"} />
              <span>{it.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-neutral-200">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#FF9418]/10 text-[#FF9418] font-bold flex items-center justify-center text-xs">
            {(user?.email || "A")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-neutral-900 truncate">{user?.name || "Admin"}</div>
            <div className="text-[11px] text-neutral-500 truncate">{user?.email}</div>
          </div>
        </div>
        <Button onClick={onLogout} variant="outline"
          className="w-full h-9 border-neutral-300 hover:border-red-400 hover:text-red-600 text-neutral-700 rounded-md text-xs justify-start"
          data-testid="admin-logout">
          <LogOut size={12} className="mr-2" /> Logout
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Desktop sidebar */}
      <div className="hidden lg:block sticky top-0 h-screen">
        <Sidebar />
      </div>
      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="bg-black/40 flex-1" onClick={() => setMobileOpen(false)} />
          <Sidebar mobile />
        </div>
      )}
      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-neutral-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} className="text-neutral-700" data-testid="admin-menu-toggle">
            <Menu size={22} />
          </button>
          <img src="/logo.png" alt="TDI" className="h-8 w-auto" />
          <button onClick={onLogout} className="text-neutral-500"><LogOut size={18} /></button>
        </div>
        <div className="p-4 md:p-6 lg:p-8 max-w-full">
          {children}
        </div>
      </main>
    </div>
  );
}

export function ProtectedAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-neutral-50 text-neutral-500 flex items-center justify-center">Loading...</div>;
  if (!user || !user.email) return <Navigate to="/admin/login" replace />;
  return <AdminShell>{children}</AdminShell>;
}

/* ============ PAGE HEADER HELPER ============ */
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display font-extrabold text-neutral-900 text-2xl md:text-3xl">{title}</h1>
        {subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

/* ============ COMMON STYLES ============ */
export const inp = "w-full bg-white border border-neutral-300 focus:border-[#FF9418] focus:ring-2 focus:ring-[#FF9418]/20 rounded-md px-3 py-2 text-sm text-neutral-900 outline-none transition";
export const btnPrimary = "bg-[#FF9418] hover:bg-[#e88410] text-white rounded-md font-semibold";
export const btnSecondary = "bg-white border border-neutral-300 hover:border-neutral-400 text-neutral-700 rounded-md font-semibold";
export const label = "text-xs font-mono uppercase tracking-wider text-neutral-500 mb-1.5 block";
export const card = "bg-white border border-neutral-200 rounded-xl";
